// frontend/src/components/StaffGuard.jsx
import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useLocation } from "react-router-dom";
import { fetchSessionBootstrap, setPortalTokenProvider, setPortalTenantSlugProvider } from "../api.js";
import { clearSentryUserContext, setSentryUserContext } from "../sentry.js";

function normalizeDomain(value) {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  return trimmed.replace(/^https?:\/\//i, "").replace(/\/$/, "");
}

export function buildPortalLoginUrl() {
  const configured = normalizeDomain(import.meta.env.VITE_PORTAL_ROOT_DOMAIN);
  if (configured) {
    return `https://${configured}/portal/login`;
  }
  if (typeof window !== "undefined" && window.location) {
    return `${window.location.origin.replace(/\/$/, "")}/portal/login`;
  }
  return "/portal/login";
}

export function redirectToPortalLogin() {
  const currentUrl = window.location.href;
  const returnUrl = encodeURIComponent(currentUrl);
  const baseUrl = getPortalLoginUrl();
  const target = `${baseUrl}?returnUrl=${returnUrl}`;
  if (typeof window !== "undefined") {
    window.location.href = target;
  }
}

// ✅ Create context for user role
const StaffRoleContext = createContext(null);

// ✅ Hook to access user role in any component
export function useStaffRole() {
  const context = useContext(StaffRoleContext);
  if (!context) {
    return { role: null, loading: true };
  }
  return context;
}

export default function StaffGuard({ children }) {
  const { isLoaded, isSignedIn, getToken, user, sessionId } = useAuth();
  const location = useLocation();
  const [state, setState] = useState({ loading: true, allowed: false, error: null, role: null });
  const [tenantContext, setTenantContext] = useState({ id: null, slug: null, name: null });

  // Determine which JWT template to use
  const tokenTemplate = "portal";

  // Check if this is an ops route (system-wide, no tenant needed)
  const isOpsRoute = location.pathname.startsWith("/ops/");

  useEffect(() => {
    // Wait for Clerk to load
    if (!isLoaded) {
      setState({ loading: true, allowed: false, error: null, role: null });
      return;
    }

    // If not signed in, redirect will be handled by App.jsx
    if (!isSignedIn) {
      setState({ loading: false, allowed: false, error: null, role: null });
      return;
    }

    let active = true;

    const ensureAccess = async (attempt = 0) => {
      try {
        // Configure token provider
        setPortalTokenProvider(async () => {
          try {
            const token = await getToken({ template: tokenTemplate });
            return token || null;
          } catch (err) {
            console.error("[StaffGuard] Failed to get token:", err);
            return null;
          }
        });

        // Get user's role from public metadata
        const userRole = user?.publicMetadata?.role || user?.unsafeMetadata?.role || null;

        // ✅ CORRECT: For ops routes, ONLY super_admin can access
        if (isOpsRoute) {
          if (userRole !== "super_admin") {
            setState({
              loading: false,
              allowed: false,
              error: new Error("Access denied. Super admin access required."),
              role: userRole,
            });
            return;
          }

          // Super admin confirmed - allow access to ops routes
          if (!active) return;
          setState({ loading: false, allowed: true, error: null, role: userRole });
          return;
        }

        // For regular staff routes: need tenant, so call session bootstrap
        setPortalTenantSlugProvider(async () => null);

        const result = await fetchSessionBootstrap();

        if (!active) return;

        if (!result || !result.tenant || !result.memberRole) {
          setState({
            loading: false,
            allowed: false,
            error: new Error("Unable to verify staff access. Please ensure you have the correct permissions."),
            role: userRole,
          });
          return;
        }

        // ✅ Use memberRole from bootstrap result for staff routes
        const tenant = result?.tenant || null;
        setTenantContext({
          id: tenant?.id ?? result?.tenantId ?? null,
          slug: tenant?.slug ?? result?.tenantSlug ?? null,
          name: tenant?.name ?? null,
        });
        setState({ loading: false, allowed: true, error: null, role: result.memberRole });
      } catch (err) {
        if (!active) return;

        const status = err?.status ?? err?.data?.status ?? null;

        // Handle authentication errors
        if (status === 401 || status === 403) {
          setState({
            loading: false,
            allowed: false,
            error: new Error("Authentication failed. Please sign in again."),
            role: null,
          });
          return;
        }

        // Handle tenant not found error
        if (status === 404 || err?.message?.includes("tenant_not_found")) {
          setState({
            loading: false,
            allowed: false,
            error: new Error("No tenant configuration found. Please contact support."),
            role: null,
          });
          return;
        }

        // Retry on transient errors
        if (err?.message === "Portal auth not configured" && attempt < 3) {
          setTimeout(
            () => {
              ensureAccess(attempt + 1);
            },
            150 * (attempt + 1),
          );
          return;
        }

        setState({
          loading: false,
          allowed: false,
          error: err instanceof Error ? err : new Error("Unable to verify access"),
          role: null,
        });
      }
    };

    ensureAccess();

    return () => {
      active = false;
    };
  }, [isLoaded, isSignedIn, getToken, user, tokenTemplate, isOpsRoute]);

  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn || !state.allowed) {
      clearSentryUserContext({ appName: "tenant" });
      return;
    }

    const displayName = user?.fullName || user?.username || null;
    const email = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress || null;

    setSentryUserContext({
      appName: "tenant",
      tenantId: tenantContext.id,
      tenantSlug: tenantContext.slug,
      tenantName: tenantContext.name,
      memberRole: state.role,
      clerkId: user?.id || null,
      sessionId,
      user: {
        id: user?.id || null,
        email,
        displayName,
      },
    });
  }, [
    isLoaded,
    isSignedIn,
    sessionId,
    state.allowed,
    state.role,
    tenantContext.id,
    tenantContext.slug,
    tenantContext.name,
    user?.id,
    user?.fullName,
    user?.username,
    user?.primaryEmailAddress?.emailAddress,
    user?.emailAddresses,
  ]);

  // Show loading state
  if (state.loading || !isLoaded) {
    return (
      <div className="portal-loading">
        <p>Preparing your workspace…</p>
      </div>
    );
  }

  // If allowed, render children with role context
  if (state.allowed) {
    return <StaffRoleContext.Provider value={{ role: state.role, loading: false }}>{children}</StaffRoleContext.Provider>;
  }

  // Show error state
  const message = (() => {
    if (!state.error) {
      return "Unable to verify your access.";
    }
    if (state.error instanceof Error) {
      return state.error.message || "Unable to verify your access.";
    }
    return state.error?.message || "Unable to verify your access.";
  })();

  return (
    <div className="portal-error">
      <h2>Access Verification Failed</h2>
      <p>{message}</p>
      <button type="button" onClick={() => window.location.reload()}>
        Retry
      </button>
      <button type="button" onClick={() => redirectToPortalLogin()} style={{ marginLeft: "8px" }}>
        Sign in again
      </button>
    </div>
  );
}
